import React, { useState } from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';

export default function AgeGate({ user, onVerified, onDenied }) {
  const [show, setShow] = useState(true);

  const userAge = (() => {
    if (!user?.date_of_birth) return null;
    const dob = new Date(user.date_of_birth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age;
  })();

  const isVerified = userAge !== null && userAge >= 18;
  const needsDOB = userAge === null;

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="age-gate-modal"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-zinc-900 border border-zinc-700 p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-rose-500/20 flex items-center justify-center">
            <Shield size={32} className="text-rose-400" />
          </div>

          <h2 className="font-bold text-2xl text-white mb-3">Age Verification Required</h2>

          {needsDOB ? (
            <>
              <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
                This section contains age-restricted content. Please add your date of birth in
                <span className="text-white font-medium"> Settings</span> to verify your age.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => { setShow(false); onDenied?.(); }}
                  className="flex-1 bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                  data-testid="age-gate-back-btn"
                >
                  Go Back
                </Button>
                <Button
                  onClick={() => { window.location.href = '/settings'; }}
                  className="flex-1 bg-rose-500 text-white hover:bg-rose-600"
                  data-testid="age-gate-settings-btn"
                >
                  Go to Settings
                </Button>
              </div>
            </>
          ) : isVerified ? (
            <>
              <p className="text-zinc-400 text-sm mb-6">
                By entering, you confirm you are <span className="text-white font-bold">18 years or older</span> and
                consent to viewing age-restricted content.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => { setShow(false); onDenied?.(); }}
                  className="flex-1 bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                  data-testid="age-gate-exit-btn"
                >
                  Exit
                </Button>
                <Button
                  onClick={() => { setShow(false); onVerified?.(); }}
                  className="flex-1 bg-rose-500 text-white hover:bg-rose-600"
                  data-testid="age-gate-enter-btn"
                >
                  I am 18+ - Enter
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 justify-center mb-4">
                <AlertTriangle size={20} className="text-amber-400" />
                <span className="text-amber-400 font-semibold">Access Denied</span>
              </div>
              <p className="text-zinc-400 text-sm mb-6">
                Based on your profile, you do not meet the minimum age requirement to access this content.
              </p>
              <Button
                onClick={() => { setShow(false); onDenied?.(); }}
                className="w-full bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                data-testid="age-gate-denied-btn"
              >
                Go Back
              </Button>
            </>
          )}

          <p className="text-zinc-600 text-xs mt-6">
            MMarketing takes content safety seriously. All creators are verified.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
